import React, {
    useEffect, useMemo, useReducer, useRef,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import {
    Box,
    Button,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import {
    useLazyQuery,
    useMutation,
    useQuery,
} from '@apollo/client';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import StorageOutlinedIcon from '@material-ui/icons/StorageOutlined';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import { FetchPolicy } from 'utils/enum/Core';
import Select from 'components/widgets/Select';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import ReportTemplateQuery from 'services/graphQL/query/setting/ReportTemplateQuery';
import ReportTemplateMutation from 'services/graphQL/mutate/setting/ReportTemplateMutation';
import CreateReportDialog from 'components/modules/settings/reportTemplate/CreateReportDialog';
import PreviewReportDialog from 'components/modules/settings/reportTemplate/PreviewReportDialog';
import ReportTemplateStyles from 'styles/modules/settings/ReportTemplateStyles';

const FREQUENCY_OPTIONS = [
    { value: 'daily', label: 'Daily' },
];

const EMPTY_REPORTS = [];
const EMPTY_RECIPIENTS = [];
const EMPTY_EMPLOYEES = [];

const formatEmployeeName = (employee) => {
    const parts = [employee.firstName, employee.middleName, employee.lastName]
        .filter((part) => !StringUtils.isEmpty(part));
    return parts.join(' ') || employee.userName || '';
};

const getNextRunLabel = (timeScheduled) => {
    if (StringUtils.isEmpty(timeScheduled)) return '—';

    const next = moment(timeScheduled, ['HH:mm', 'H:mm'], true);
    if (!next.isValid()) return '—';

    return next.format('h:mm A');
};

const formatFrequencyLabel = (frequency) => {
    if (StringUtils.isEmpty(frequency)) return '—';
    return frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();
};

const highlightSql = (query = '') => {
    if (!query) return null;

    // eslint-disable-next-line max-len
    const tokenRegex = /('(?:''|[^'])*')|\b(SELECT|FROM|WHERE|AND|OR|AS|WITH|NOLOCK|COUNT|GETDATE|CONVERT|CASE|WHEN|THEN|ELSE|END|INNER|LEFT|RIGHT|JOIN|ON|GROUP|ORDER|BY|TOP|DISTINCT|NULL|NOT|IN|EXISTS)\b|\b(\d+)\b/gi;

    const parts = [];
    let lastIndex = 0;
    let match = tokenRegex.exec(query);
    let key = 0;

    while (match !== null) {
        if (match.index > lastIndex) {
            parts.push(query.slice(lastIndex, match.index));
        }

        let className;
        if (match[1]) {
            className = 'sql-string';
        } else if (match[2]) {
            className = 'sql-keyword';
        } else {
            className = 'sql-number';
        }

        parts.push(
            <span key={`sql-token-${key}`} className={className}>
                {match[0]}
            </span>,
        );
        key += 1;

        lastIndex = tokenRegex.lastIndex;
        match = tokenRegex.exec(query);
    }

    if (lastIndex < query.length) {
        parts.push(query.slice(lastIndex));
    }

    return parts;
};

const useStyles = makeStyles((theme) => ReportTemplateStyles.content(theme));

const DEFAULT_DRAFT = {
    name: '',
    query: '',
    content: '',
    frequency: 'daily',
    timeScheduled: '09:00',
};

const INIT_STATE = {
    selectedReportId: null,
    createOpen: false,
    previewOpen: false,
    deleteId: null,
    selectedEmployeeId: null,
    draft: { ...DEFAULT_DRAFT },
};

const ACTION_TYPES = {
    SET_SELECTED_REPORT: 'setSelectedReport',
    SET_CREATE_OPEN: 'setCreateOpen',
    SET_PREVIEW_OPEN: 'setPreviewOpen',
    SET_DELETE_ID: 'setDeleteId',
    SET_SELECTED_EMPLOYEE: 'setSelectedEmployee',
    SET_DRAFT: 'setDraft',
    CHANGE_DRAFT_FIELD: 'changeDraftField',
    RESET_DETAIL: 'resetDetail',
};

const reducer = (state, action = {}) => {
    switch (action.type) {
    case ACTION_TYPES.SET_SELECTED_REPORT:
        return {
            ...state,
            selectedReportId: action.value,
            selectedEmployeeId: null,
        };
    case ACTION_TYPES.SET_CREATE_OPEN:
        return {
            ...state,
            createOpen: action.value,
        };
    case ACTION_TYPES.SET_PREVIEW_OPEN:
        return {
            ...state,
            previewOpen: action.value,
        };
    case ACTION_TYPES.SET_DELETE_ID:
        return {
            ...state,
            deleteId: action.value,
        };
    case ACTION_TYPES.SET_SELECTED_EMPLOYEE:
        return {
            ...state,
            selectedEmployeeId: action.value,
        };
    case ACTION_TYPES.SET_DRAFT:
        return {
            ...state,
            draft: action.value,
        };
    case ACTION_TYPES.CHANGE_DRAFT_FIELD:
        return {
            ...state,
            draft: {
                ...state.draft,
                [action.value.name]: action.value.value,
            },
        };
    case ACTION_TYPES.RESET_DETAIL:
        return {
            ...state,
            selectedReportId: null,
            selectedEmployeeId: null,
            draft: { ...DEFAULT_DRAFT },
        };
    default:
        return state;
    }
};

const ReportTemplateSettings = ({ canWrite }) => {
    const classes = useStyles();
    const queryScrollRef = useRef(null);
    const queryLinesRef = useRef(null);
    const [state, dispatch] = useReducer(reducer, INIT_STATE);
    const {
        selectedReportId,
        createOpen,
        previewOpen,
        deleteId,
        selectedEmployeeId,
        draft,
    } = state;

    const {
        data: listData,
        loading: loadingList,
        refetch: refetchList,
        error: listError,
    } = useQuery(ReportTemplateQuery.REPORT_TEMPLATE_LIST, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: detailData,
        loading: loadingDetail,
        error: detailError,
        refetch: refetchDetail,
    } = useQuery(ReportTemplateQuery.REPORT_TEMPLATE_FIND, {
        variables: { id: selectedReportId },
        skip: !selectedReportId,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: employeesData,
        error: employeesError,
    } = useQuery(ReportTemplateQuery.LIST_EMPLOYEES, {
        // Empty query matches all; exclude [0] avoids Sequelize NOT IN () with empty array
        variables: { query: '', exclude: [0] },
        skip: !canWrite,
        fetchPolicy: FetchPolicy.CACHE_FIRST,
    });

    const [
        loadPreview,
        {
            data: previewData,
            loading: loadingPreview,
            error: previewError,
        },
    ] = useLazyQuery(ReportTemplateQuery.REPORT_TEMPLATE_PREVIEW, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [updateReport, { loading: saving }] = useMutation(ReportTemplateMutation.REPORT_TEMPLATE_UPDATE, {
        onCompleted: (response) => {
            if (response?.reportTemplateUpdate?.success) {
                ModalUtils.successMessage(null, 'Report updated successfully');
                refetchList();
                refetchDetail();
                return;
            }
            ModalUtils.errorMessage(null, 'Only queries of type SELECT are allowed.');
        },
        onError: (error) => {
            ModalUtils.errorMessage(error?.graphQLErrors, error?.message);
        },
    });

    const [removeReport, { loading: deleting }] = useMutation(ReportTemplateMutation.REPORT_TEMPLATE_REMOVE, {
        onCompleted: (response) => {
            if (response?.reportTemplateRemove?.success) {
                ModalUtils.successMessage(null, 'Report deleted successfully');
                dispatch({ type: ACTION_TYPES.SET_DELETE_ID, value: null });
                if (selectedReportId === deleteId) {
                    dispatch({ type: ACTION_TYPES.RESET_DETAIL });
                }
                refetchList();
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(error?.graphQLErrors, error?.message);
        },
    });

    const [addRecipient, { loading: addingRecipient }] = useMutation(ReportTemplateMutation.REPORT_TEMPLATE_USER_CREATE, {
        onCompleted: (response) => {
            if (response?.reportTemplateUserCreate?.success) {
                dispatch({ type: ACTION_TYPES.SET_SELECTED_EMPLOYEE, value: null });
                refetchList();
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(error?.graphQLErrors, error?.message);
        },
    });

    const [removeRecipient] = useMutation(ReportTemplateMutation.REPORT_TEMPLATE_USER_REMOVE, {
        onCompleted: (response) => {
            if (response?.reportTemplateUserRemove?.success) {
                refetchList();
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(error?.graphQLErrors, error?.message);
        },
    });

    const reports = listData?.reportTemplateList || EMPTY_REPORTS;
    const employees = employeesData?.listEmployees || EMPTY_EMPLOYEES;
    const selectedReport = reports.find((item) => item.id === selectedReportId) || null;
    const recipients = selectedReport?.users || EMPTY_RECIPIENTS;
    const detail = detailData?.reportTemplateFind;
    const detailMatchesSelection = Boolean(
        selectedReportId
        && detail
        && detail.id === selectedReportId,
    );
    const canEditSelected = Boolean(canWrite && detailMatchesSelection && !loadingDetail);
    const queryLines = useMemo(() => (draft.query || '').split('\n'), [draft.query]);
    const nextRunLabel = getNextRunLabel(draft.timeScheduled);

    const recipientUserIds = useMemo(
        () => new Set(recipients.map((item) => item.usersTableUserId)),
        [recipients],
    );

    const employeeSelectOptions = useMemo(() => (
        employees
            .filter((employee) => !recipientUserIds.has(employee.userId))
            .map((employee) => ({
                value: employee.userId,
                label: `${formatEmployeeName(employee)}${employee.employeeType ? ` (${employee.employeeType})` : ''}`,
            }))
    ), [employees, recipientUserIds]);

    useEffect(() => {
        if (listError) ModalUtils.errorMessage(listError?.graphQLErrors);
    }, [listError]);

    useEffect(() => {
        if (detailError) ModalUtils.errorMessage(detailError?.graphQLErrors);
    }, [detailError]);

    useEffect(() => {
        if (employeesError) ModalUtils.errorMessage(employeesError?.graphQLErrors);
    }, [employeesError]);

    useEffect(() => {
        if (previewError) ModalUtils.errorMessage(previewError?.graphQLErrors, previewError?.message);
    }, [previewError]);

    useEffect(() => {
        if (!selectedReportId) {
            dispatch({
                type: ACTION_TYPES.SET_DRAFT,
                value: { ...DEFAULT_DRAFT },
            });
            dispatch({ type: ACTION_TYPES.SET_SELECTED_EMPLOYEE, value: null });
            return;
        }

        if (!detail || detail.id !== selectedReportId) {
            // Drop stale Apollo data from a previous selection while the new find is in flight
            dispatch({
                type: ACTION_TYPES.SET_DRAFT,
                value: { ...DEFAULT_DRAFT },
            });
            return;
        }

        dispatch({
            type: ACTION_TYPES.SET_DRAFT,
            value: {
                name: detail.name || '',
                query: detail.query || '',
                content: detail.content || '',
                frequency: detail.frequency || 'daily',
                timeScheduled: detail.timeScheduled || '09:00',
            },
        });
    }, [detail, selectedReportId]);

    const onAddRecipient = () => {
        if (!canEditSelected || !selectedEmployeeId) return;

        addRecipient({
            variables: {
                reportTemplateId: selectedReportId,
                usersTableUserId: Number(selectedEmployeeId),
            },
        });
    };

    const onSaveChanges = () => {
        if (!canEditSelected || !detailMatchesSelection) return;

        const name = draft.name?.trim() || '';
        const query = draft.query?.trim() || '';
        const content = draft.content?.trim() || '';
        const frequency = draft.frequency?.trim() || '';
        const timeScheduled = draft.timeScheduled?.trim() || '';

        if (
            StringUtils.isEmpty(name)
            || StringUtils.isEmpty(query)
            || StringUtils.isEmpty(content)
            || StringUtils.isEmpty(frequency)
            || StringUtils.isEmpty(timeScheduled)
        ) {
            ModalUtils.errorMessage(null, 'Name, query, template, frequency and time are required.');
            return;
        }

        updateReport({
            variables: {
                id: selectedReportId,
                name,
                query,
                content,
                frequency,
                timeScheduled,
            },
        });
    };

    const onPreviewTemplate = () => {
        if (!selectedReportId || loadingDetail) return;

        const query = draft.query?.trim() || '';
        const content = draft.content?.trim() || '';

        if (StringUtils.isEmpty(query) || StringUtils.isEmpty(content)) {
            ModalUtils.errorMessage(null, 'Query and template are required to preview.');
            return;
        }

        dispatch({ type: ACTION_TYPES.SET_PREVIEW_OPEN, value: true });
        loadPreview({
            variables: {
                content,
                query,
            },
        });
    };

    const onSelectReport = (reportId) => {
        dispatch({
            type: ACTION_TYPES.SET_SELECTED_REPORT,
            value: reportId,
        });
    };

    const onDraftChange = (field, value) => {
        dispatch({
            type: ACTION_TYPES.CHANGE_DRAFT_FIELD,
            value: { name: field, value },
        });
    };

    const syncQueryScroll = (event) => {
        const { scrollTop, scrollLeft } = event.target;
        if (queryScrollRef.current) {
            queryScrollRef.current.scrollTop = scrollTop;
            queryScrollRef.current.scrollLeft = scrollLeft;
        }
        if (queryLinesRef.current) {
            queryLinesRef.current.scrollTop = scrollTop;
        }
    };

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography className={classes.headerTitle} component="div">
                    <DescriptionOutlinedIcon className={classes.headerIcon} />
                    <span>Reports</span>
                    <span className={classes.headerDivider}>/</span>
                    <span className={classes.headerReportName}>
                        {selectedReport ? selectedReport.name : 'Select a report'}
                    </span>
                </Typography>
                {canWrite && (
                    <Button
                        className={classes.newButton}
                        startIcon={<AddIcon />}
                        onClick={() => dispatch({ type: ACTION_TYPES.SET_CREATE_OPEN, value: true })}
                    >
                        New Report
                    </Button>
                )}
            </Box>

            <Paper className={classes.reportsCard} elevation={0}>
                <Box className={classes.cardHeader}>
                    <Box className={classes.cardTitleRow}>
                        <Typography className={classes.cardTitle}>Configured Reports</Typography>
                        <span className={classes.countBadge}>{reports.length}</span>
                    </Box>
                </Box>
                <Box className={classes.reportsTableWrap}>
                    <Table size="small" className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Report Name</TableCell>
                                <TableCell>Recipients</TableCell>
                                <TableCell>Frequency</TableCell>
                                <TableCell>Next Run</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingList && (
                                <TableRow>
                                    <TableCell colSpan={5}>Loading reports...</TableCell>
                                </TableRow>
                            )}
                            {!loadingList && reports.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>No reports configured</TableCell>
                                </TableRow>
                            )}
                            {reports.map((report) => {
                                const isSelected = report.id === selectedReportId;
                                const count = report.users?.length || 0;
                                return (
                                    <TableRow
                                        key={report.id}
                                        hover
                                        tabIndex={0}
                                        aria-selected={isSelected}
                                        className={clsx(classes.clickableRow, isSelected && classes.selectedRow)}
                                        onClick={() => onSelectReport(report.id)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                onSelectReport(report.id);
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <span className={classes.reportNameCell}>
                                                <DescriptionOutlinedIcon
                                                    className={clsx(
                                                        classes.reportDocIcon,
                                                        isSelected && classes.selectedReportDocIcon,
                                                    )}
                                                />
                                                {report.name}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={classes.recipientsPill}>
                                                <PeopleOutlineIcon className={classes.recipientsIcon} />
                                                {`${count} users`}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {formatFrequencyLabel(report.frequency)}
                                        </TableCell>
                                        <TableCell>
                                            <span className={classes.nextRunCell}>
                                                {getNextRunLabel(report.timeScheduled)}
                                            </span>
                                        </TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            {canWrite && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        className={clsx(classes.actionIcon, isSelected && classes.actionIconActive)}
                                                        onClick={() => dispatch({
                                                            type: ACTION_TYPES.SET_DELETE_ID,
                                                            value: report.id,
                                                        })}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
                <Box className={classes.tableFooter}>
                    {`Showing ${reports.length} of ${reports.length} reports`}
                </Box>
            </Paper>

            <Box width="100%" maxWidth="100%" boxSizing="border-box">
                <Grid container spacing={2} className={classes.detailGrid}>
                    <Grid item xs={12} md={6}>
                        <Paper className={clsx(classes.detailCard, classes.recipientsCard)} elevation={0}>
                            <Box className={classes.cardHeader}>
                                <Box>
                                    <Typography className={classes.cardTitle}>Send to Recipients</Typography>
                                    <Typography className={classes.cardSubtitle}>Scheduled automated SMS</Typography>
                                </Box>
                            </Box>

                            {canWrite && (
                                <Box className={classes.searchRow}>
                                    <Box flex={1}>
                                        <Select
                                            size="sm"
                                            name="employee"
                                            maxMenuHeight={130}
                                            placeholder="Search employee..."
                                            value={selectedEmployeeId}
                                            options={employeeSelectOptions}
                                            disabled={!canEditSelected}
                                            onChange={(_, value) => dispatch({
                                                type: ACTION_TYPES.SET_SELECTED_EMPLOYEE,
                                                value,
                                            })}
                                        />
                                    </Box>
                                    <Button
                                        className={classes.addButton}
                                        startIcon={<AddIcon />}
                                        disabled={!canEditSelected || !selectedEmployeeId || addingRecipient}
                                        onClick={onAddRecipient}
                                    >
                                        Add
                                    </Button>
                                </Box>
                            )}

                            <Box className={classes.recipientsTableWrap}>
                                {!selectedReportId ? (
                                    <Box className={classes.placeholderBox}>
                                        Select a report to manage recipients and schedule.
                                    </Box>
                                ) : (
                                    <Table size="small" className={classes.table}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>ID</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Employee Type</TableCell>
                                                <TableCell>Cell Phone</TableCell>
                                                {canWrite && <TableCell align="right">Actions</TableCell>}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recipients.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={canWrite ? 5 : 4}>No recipients</TableCell>
                                                </TableRow>
                                            )}
                                            {recipients.map((recipient) => (
                                                <TableRow key={recipient.id}>
                                                    <TableCell>{recipient.usersTableUserId}</TableCell>
                                                    <TableCell>{recipient.name}</TableCell>
                                                    <TableCell>
                                                        {recipient.employeeType ? (
                                                            <span className={classes.typeBadge}>{recipient.employeeType}</span>
                                                        ) : '—'}
                                                    </TableCell>
                                                    <TableCell>{recipient.cellPhone || '—'}</TableCell>
                                                    {canWrite && (
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                className={clsx(classes.actionIcon, classes.actionIconActive)}
                                                                disabled={!canEditSelected}
                                                                onClick={() => removeRecipient({ variables: { id: recipient.id } })}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>

                            <Typography className={classes.recipientsFooter}>
                                {selectedReportId
                                    ? `${recipients.length} active recipients`
                                    : '0 active recipients'}
                            </Typography>

                            <Box className={classes.scheduleRow}>
                                <Box className={classes.scheduleField}>
                                    <Typography className={classes.scheduleLabel} component="div">
                                        Frequency
                                    </Typography>
                                    <Select
                                        size="sm"
                                        name="frequency"
                                        value={draft.frequency}
                                        options={FREQUENCY_OPTIONS}
                                        disabled={!canEditSelected || loadingDetail}
                                        onChange={(name, value) => onDraftChange(name, value)}
                                    />
                                </Box>
                                <Box className={classes.scheduleField}>
                                    <Typography className={classes.scheduleLabel} component="div">
                                        At
                                    </Typography>
                                    <TextField
                                        id="report-at"
                                        type="time"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        className={classes.timeInput}
                                        value={draft.timeScheduled}
                                        disabled={!canEditSelected || loadingDetail}
                                        onChange={(e) => onDraftChange('timeScheduled', e.target.value)}
                                        inputProps={{ step: 300 }}
                                        // eslint-disable-next-line react/jsx-no-duplicate-props
                                        InputProps={{
                                            endAdornment: <ScheduleOutlinedIcon fontSize="small" className={classes.muted} />,
                                        }}
                                    />
                                </Box>
                                <Typography className={classes.scheduleHint}>
                                    {selectedReportId
                                        ? `Next run scheduled for ${nextRunLabel.toLowerCase()}`
                                        : 'Select a report to configure the schedule.'}
                                </Typography>
                                <Box className={classes.scheduleActions}>
                                    <Button
                                        className={classes.previewButton}
                                        variant="outlined"
                                        startIcon={<VisibilityOutlinedIcon />}
                                        disabled={!selectedReportId || loadingDetail || loadingPreview}
                                        onClick={onPreviewTemplate}
                                    >
                                        {loadingPreview ? 'Loading...' : 'Preview'}
                                    </Button>
                                    {canWrite && (
                                        <Button
                                            className={classes.saveButton}
                                            disabled={!canEditSelected || saving || loadingDetail}
                                            onClick={onSaveChanges}
                                        >
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper className={clsx(classes.detailCard, classes.queryCard)} elevation={0}>
                            <Box className={classes.cardHeader}>
                                <Box className={classes.cardTitleRow}>
                                    <StorageOutlinedIcon className={classes.sectionIcon} />
                                    <Typography className={classes.cardTitle}>Report Query</Typography>
                                </Box>
                            </Box>
                            <Box className={classes.queryWrap}>
                                <Box className={classes.queryLines} ref={queryLinesRef}>
                                    {(selectedReportId ? queryLines : ['']).map((_, index) => (
                                        <span key={`line-${index + 1}`}>{index + 1}</span>
                                    ))}
                                </Box>
                                <pre
                                    ref={queryScrollRef}
                                    className={classes.queryHighlight}
                                    aria-hidden
                                >
                                    {selectedReportId ? highlightSql(draft.query) : null}
                                    {'\n'}
                                </pre>
                                <textarea
                                    className={classes.queryTextarea}
                                    value={selectedReportId ? draft.query : ''}
                                    disabled={!canEditSelected || loadingDetail}
                                    placeholder={!selectedReportId ? 'Select a report to view and edit the SQL query.' : ''}
                                    spellCheck={false}
                                    onScroll={syncQueryScroll}
                                    onChange={(e) => onDraftChange('query', e.target.value)}
                                />
                            </Box>
                        </Paper>

                        <Paper className={clsx(classes.detailCard, classes.templateCard)} elevation={0}>
                            <Box className={classes.cardHeader}>
                                <Box className={classes.cardTitleRow}>
                                    <ChatBubbleOutlineIcon className={classes.sectionIcon} />
                                    <Typography className={classes.cardTitle}>
                                        {`Report Template (${selectedReportId ? draft.content.length : 0}/255)`}
                                    </Typography>
                                </Box>
                            </Box>
                            <textarea
                                className={classes.templateEditor}
                                maxLength={255}
                                value={selectedReportId ? draft.content : ''}
                                disabled={!canEditSelected || loadingDetail}
                                placeholder={!selectedReportId ? 'Select a report to edit the SMS template.' : ''}
                                onChange={(e) => onDraftChange('content', e.target.value)}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            <CreateReportDialog
                open={createOpen}
                onClose={() => dispatch({ type: ACTION_TYPES.SET_CREATE_OPEN, value: false })}
                onCreated={() => refetchList()}
            />

            <PreviewReportDialog
                open={previewOpen}
                onClose={() => dispatch({ type: ACTION_TYPES.SET_PREVIEW_OPEN, value: false })}
                loading={loadingPreview}
                messages={previewData?.reportTemplatePreview?.messages || []}
                totalCount={previewData?.reportTemplatePreview?.totalCount || 0}
                reportName={draft.name || selectedReport?.name || ''}
            />

            <ConfirmDialog
                open={Boolean(deleteId)}
                title="Delete Report"
                description="Are you sure you want to delete this report template?"
                titlePrimary="Delete"
                titleSecondary="Cancel"
                variant="outlined"
                onClose={() => dispatch({ type: ACTION_TYPES.SET_DELETE_ID, value: null })}
                onClickSecondary={() => dispatch({ type: ACTION_TYPES.SET_DELETE_ID, value: null })}
                onClickPrimary={() => removeReport({ variables: { id: deleteId } })}
                disablePrimaryButton={deleting}
                disableSecondaryButton={deleting}
            />
        </Box>
    );
};

ReportTemplateSettings.propTypes = {
    canWrite: PropTypes.bool,
};

ReportTemplateSettings.defaultProps = {
    canWrite: false,
};

export default ReportTemplateSettings;
